import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import SoulBalmLogo from '../components/SoulBalmLogo';
import api from '../lib/api';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleReset = async () => {
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to reset password. The link may be expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#070A1A] px-4" data-testid="reset-password-page">
        <div className="absolute inset-0 mystic-gradient" />
        <div className="relative z-10 text-center">
          <AlertCircle size={48} className="text-[#C87886] mx-auto mb-4" />
          <h2 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0] mb-3">Invalid Reset Link</h2>
          <p className="text-sm text-[#BDB2C4] mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/login" className="btn-primary" data-testid="back-to-login">Back to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#070A1A] px-4" data-testid="reset-password-page">
      <div className="absolute inset-0 mystic-gradient" />
      <motion.div className="relative z-10 w-full max-w-md" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center mb-8">
          <SoulBalmLogo size={48} className="mx-auto mb-4" />
        </div>

        {!success ? (
          <div className="glass-card p-8">
            <h2 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0] mb-2 text-center">Create a New Password</h2>
            <p className="text-sm text-[#BDB2C4] mb-6 text-center">Choose a new sacred key for your Tarot Room.</p>
            <div className="space-y-4">
              <div className="relative">
                <Input data-testid="reset-password" type={showPass ? "text" : "password"} placeholder="New password (min 6 characters)"
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="glass-input h-12 rounded-xl px-4 pr-12 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37]" />
                <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-3 text-[#BDB2C4]" data-testid="reset-toggle-password">
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <Input data-testid="reset-confirm-password" type={showPass ? "text" : "password"} placeholder="Confirm new password"
                value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                className="glass-input h-12 rounded-xl px-4 text-[#FFFFF0] placeholder:text-[#BDB2C4]/50 bg-white/5 border-white/10 focus:border-[#D4AF37]" />
              {error && <p className="text-sm text-[#C87886]" data-testid="reset-error">{error}</p>}
              <button onClick={handleReset} disabled={loading || !password || !confirmPassword}
                className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-30" data-testid="reset-submit-btn">
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        ) : (
          <div className="glass-card p-8 text-center">
            <CheckCircle size={48} className="text-[#D4AF37] mx-auto mb-4" />
            <h2 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0] mb-3">Password Reset Complete</h2>
            <p className="text-sm text-[#BDB2C4] mb-6">Your new sacred key has been set. You can now log in with your new password.</p>
            <Link to="/login" className="btn-primary inline-block" data-testid="reset-go-to-login">Enter the Room</Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
